import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../config/axios';

const formatPrice = (price) =>
  Number(price || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

function ShoppingCartPage() {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCartItems = async () => {
      try {
        const { data } = await axiosInstance.get('/api/cart');
        const validItems = (data.items || []).filter((item) => item.product);
        setCartItems(validItems.map((item) => ({ ...item, removeQuantity: 1 })));
      } catch (err) {
        if (err.response?.status === 404) {
          setCartItems([]);
        } else {
          setError('Failed to load cart items.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCartItems();
  }, []);

  const subtotal = useMemo(
    () => cartItems.reduce((acc, item) => acc + item.quantity * item.product.price, 0),
    [cartItems]
  );

  const handleRemove = async (productId, removeQuantity) => {
    try {
      await axiosInstance.put(`/api/cart/remove/${productId}`, { removeQuantity });
      setCartItems((prevItems) =>
        prevItems
          .map((item) =>
            item.product._id === productId
              ? { ...item, quantity: item.quantity - removeQuantity, removeQuantity: 1 }
              : item
          )
          .filter((item) => item.quantity > 0)
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove item from cart.');
    }
  };

  const updateRemoveQuantity = (productId, value) => {
    setCartItems((prevItems) =>
      prevItems.map((item) => {
        if (item.product._id !== productId) return item;
        const nextValue = Math.min(item.quantity, Math.max(1, Number(value) || 1));
        return { ...item, removeQuantity: nextValue };
      })
    );
  };

  if (loading) return <div className="page-shell"><div className="state-card">Loading your cart...</div></div>;

  return (
    <div className="page-shell">
      <section className="page-heading">
        <span className="eyebrow">Shopping cart</span>
        <h1>Review your items before checkout.</h1>
      </section>

      {error && <div className="notice error">{error}</div>}

      {cartItems.length === 0 ? (
        <div className="state-card">
          <h2>Your cart is empty</h2>
          <p>Find something useful and add it here when you are ready.</p>
          <Link className="button button-primary" to="/">Shop listings</Link>
        </div>
      ) : (
        <section className="cart-layout">
          <div className="cart-items">
            {cartItems.map((item) => (
              <article key={item.product._id} className="cart-item">
                <img src={item.product.images?.[0]} alt={item.product.name} />
                <div className="cart-item-main">
                  <Link to={`/product/${item.product._id}`}>{item.product.name}</Link>
                  <span>{formatPrice(item.product.price)} each</span>
                  <span>{item.quantity} in cart</span>
                </div>
                <div className="cart-controls">
                  <label>
                    Remove
                    <input
                      type="number"
                      min="1"
                      max={item.quantity}
                      value={item.removeQuantity || 1}
                      onChange={(e) => updateRemoveQuantity(item.product._id, e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="button button-danger"
                    onClick={() => handleRemove(item.product._id, item.removeQuantity || 1)}
                  >
                    Remove
                  </button>
                </div>
                <strong>{formatPrice(item.quantity * item.product.price)}</strong>
              </article>
            ))}
          </div>

          <aside className="summary-card">
            <h2>Order Summary</h2>
            <div>
              <span>Items</span>
              <strong>{cartItems.reduce((acc, item) => acc + item.quantity, 0)}</strong>
            </div>
            <div>
              <span>Subtotal</span>
              <strong>{formatPrice(subtotal)}</strong>
            </div>
            <div>
              <span>Estimated delivery</span>
              <strong>Seller arranged</strong>
            </div>
            <button type="button" className="button button-primary button-full" disabled>
              Checkout Coming Soon
            </button>
          </aside>
        </section>
      )}
    </div>
  );
}

export default ShoppingCartPage;
